import { z } from "zod";
import {
  CheckoutResponseSchema,
  EpisodeManifestSchema,
  EpisodesResponseSchema,
  ExtrasResponseSchema,
  FEED_NEXT_IN_HEADER,
  FEED_NEXT_KIND_HEADER,
  HeroPublicSchema,
  MeSchema,
  MESSAGE_IDEMPOTENCY_HEADER,
  MessageAcceptedSchema,
  PortalResponseSchema,
  ROUTES,
  TipResponseSchema,
  VoiceUploadResponseSchema,
  type Me,
} from "@scrile/contracts";
import { parseFeedItems, parseNextHint, type FeedPage } from "../messenger/feedItems";
import { ApiError, errorFromResponse, parseRetryAfter } from "./errors";
import type { FeedRequest, HeroApi } from "./types";

/** Пустой long-poll сервер держит до 25 с; запросу — запас сверху на сеть. */
export const FEED_WAIT_TIMEOUT_MS = 40_000;
/** Загрузка голосового до 2 МБ по мобильной сети — не 15 с. */
const UPLOAD_TIMEOUT_MS = 60_000;

/**
 * HTTP-клиент сайта героя по ROUTES контракта.
 *
 * - credentials: "include" — сессия одна на платформу, кука на базовом домене;
 * - x-hero — только в разработке: на проде героя определяет Host;
 * - ответы проверяются схемами контракта. В dev расхождение — ошибка на экране и в
 *   консоли (ловим сразу, пока сервер пишется параллельно); на проде — предупреждение
 *   в консоли и ответ как есть: лишнее поле сервера не должно ронять витрину.
 */

export interface HttpApiOptions {
  /** Слаг для заголовка x-hero; null — не слать. */
  heroSlug: string | null;
  strict: boolean;
  baseUrl?: string;
  fetch?: typeof fetch;
  timeoutMs?: number;
  warn?: (message: string, detail: unknown) => void;
}

type Method = "GET" | "POST";

export function createHttpApi(options: HttpApiOptions): HeroApi {
  const baseUrl = options.baseUrl ?? "";
  const timeoutMs = options.timeoutMs ?? 15_000;
  const warn = options.warn ?? ((message, detail) => console.warn(message, detail));
  const doFetch = options.fetch ?? ((input, init) => fetch(input, init));

  interface RequestOptions {
    /** JSON-тело. */
    body?: unknown;
    /** multipart: content-type с границей ставит сам браузер. */
    form?: FormData;
    headers?: Record<string, string>;
    /** Внешняя отмена (long-poll: вкладка скрыта). */
    signal?: AbortSignal;
    timeoutMs?: number;
    keepalive?: boolean;
  }

  async function request(method: Method, path: string, opts: RequestOptions = {}): Promise<{ data: unknown; response: Response }> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? timeoutMs);
    const outer = opts.signal;
    const onOuterAbort = () => controller.abort();
    if (outer) {
      if (outer.aborted) controller.abort();
      else outer.addEventListener("abort", onOuterAbort, { once: true });
    }
    const headers: Record<string, string> = { accept: "application/json", ...(opts.headers ?? {}) };
    // content-type только при теле: иначе сервер справедливо ждёт JSON и отвечает 400
    if (opts.body !== undefined) headers["content-type"] = "application/json";
    if (options.heroSlug) headers["x-hero"] = options.heroSlug;

    let response: Response;
    try {
      response = await doFetch(`${baseUrl}${path}`, {
        method,
        credentials: "include",
        headers,
        signal: controller.signal,
        ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : opts.form ? { body: opts.form } : {}),
        // Событие перед уходом на Stripe иначе обрывается вместе со страницей
        ...(opts.keepalive ? { keepalive: true } : {}),
      });
    } catch {
      throw new ApiError("network", 0);
    } finally {
      clearTimeout(timer);
      outer?.removeEventListener("abort", onOuterAbort);
    }

    const text = await response.text().catch(() => "");
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (!response.ok) {
      const error = errorFromResponse(response.status, data);
      if (response.status === 429) error.retryAfterSec = parseRetryAfter(response.headers.get("retry-after"));
      if (error.serverMessage) warn(`${method} ${path} → ${response.status} ${error.code}`, error.serverMessage);
      throw error;
    }
    return { data, response };
  }

  async function send(method: Method, path: string, body?: unknown, keepalive = false): Promise<unknown> {
    const { data } = await request(method, path, { ...(body !== undefined ? { body } : {}), keepalive });
    return data;
  }

  function check<T>(schema: z.ZodType<T, z.ZodTypeDef, unknown>, data: unknown, what: string, required = false): T {
    const result = schema.safeParse(data);
    if (result.success) return result.data;
    warn(`Response of ${what} does not match @scrile/contracts`, result.error.issues);
    if (options.strict || required) throw new ApiError("bad_response", 200, result.error.message);
    return data as T;
  }

  async function get<T>(path: string, schema: z.ZodType<T, z.ZodTypeDef, unknown>): Promise<T> {
    return check(schema, await send("GET", path), `GET ${path}`);
  }

  async function post<T>(path: string, body: unknown, schema: z.ZodType<T, z.ZodTypeDef, unknown>): Promise<T> {
    return check(schema, await send("POST", path, body), `POST ${path}`);
  }

  async function me(): Promise<Me | null> {
    try {
      return await get(ROUTES.me, MeSchema.nullable());
    } catch (error) {
      if (error instanceof ApiError && error.code === "unauthorized") return null;
      throw error;
    }
  }

  /**
   * Вход и регистрация по контракту отвечают тем же Me, что и /me. Если тело вдруг
   * не Me (старый сервер, прокси), спрашиваем /me сами: кука к этому моменту стоит.
   */
  async function authenticate(path: string, body: unknown): Promise<Me> {
    const data = await send("POST", path, body);
    const direct = MeSchema.safeParse(data);
    if (direct.success) return direct.data;
    const current = await me();
    if (!current) throw new ApiError("bad_response", 200, "Signed in, but /me is empty");
    return current;
  }

  /**
   * Лента: элементы проверяются по одному (feedItems.parseFeedItems) — незнакомый вид от
   * сервера новее клиента выбрасывается, а не роняет переписку; seq его курсор учитывает.
   */
  async function feed(req: FeedRequest): Promise<FeedPage> {
    const path = `${ROUTES.feed}?after=${Math.max(0, Math.floor(req.after))}&wait=${req.wait ? 1 : 0}`;
    const { data, response } = await request("GET", path, {
      ...(req.signal ? { signal: req.signal } : {}),
      timeoutMs: req.wait ? FEED_WAIT_TIMEOUT_MS : timeoutMs,
    });
    const parsed = parseFeedItems(data);
    if (!parsed) {
      warn(`Response of GET ${ROUTES.feed} is not an array`, data);
      throw new ApiError("bad_response", response.status, "Feed is not an array");
    }
    if (parsed.dropped > 0) warn(`GET ${ROUTES.feed}: ${parsed.dropped} item(s) do not match @scrile/contracts`, data);
    const hint = parseNextHint(response.headers.get(FEED_NEXT_IN_HEADER), response.headers.get(FEED_NEXT_KIND_HEADER));
    return { items: parsed.items, count: parsed.count, maxSeq: parsed.maxSeq, ...hint };
  }

  return {
    hero: () => get(ROUTES.hero, HeroPublicSchema),
    me,
    register: (body) => authenticate(ROUTES.register, body),
    login: (body) => authenticate(ROUTES.login, body),
    logout: async () => {
      await send("POST", ROUTES.logout, {});
    },
    episodes: () => get(ROUTES.episodes, EpisodesResponseSchema),
    episode: (ordinal) => get(ROUTES.episode(ordinal), EpisodeManifestSchema),
    startEpisode: async (ordinal) => { await send("POST", ROUTES.episodeStart(ordinal), {}); },
    extras: () => get(ROUTES.extras, ExtrasResponseSchema),
    touch: (code) => {
      send("POST", ROUTES.touch, { code }).catch((error: unknown) => {
        // Потерянное касание — потерянная привязка, но не повод мешать человеку
        warn(`POST ${ROUTES.touch} failed`, error);
      });
    },
    checkout: (body) => post(ROUTES.checkout, body, CheckoutResponseSchema),
    portal: () => post(ROUTES.portal, {}, PortalResponseSchema),
    tip: (body) => post(ROUTES.tips, body, TipResponseSchema),
    event: (body) => {
      send("POST", ROUTES.events, body, true).catch(() => {
        // Аналитика не должна мешать слушать: потерянное событие дешевле ошибки на экране
      });
    },
    feed,
    sendMessage: async (body, opts = {}) => {
      const { data } = await request("POST", ROUTES.messages, {
        body,
        ...(opts.idempotencyKey ? { headers: { [MESSAGE_IDEMPOTENCY_HEADER]: opts.idempotencyKey } } : {}),
      });
      return check(MessageAcceptedSchema, data, `POST ${ROUTES.messages}`, true);
    },
    uploadVoice: async (upload) => {
      const form = new FormData();
      form.append("durationMs", String(Math.max(0, Math.round(upload.durationMs))));
      // Файл — последним полем: сервер с потоковым разбором читает поля до файла
      form.append("file", upload.blob, upload.filename);
      const { data } = await request("POST", ROUTES.messageVoice, { form, timeoutMs: UPLOAD_TIMEOUT_MS });
      return check(VoiceUploadResponseSchema, data, `POST ${ROUTES.messageVoice}`, true);
    },
  };
}
